import React, { forwardRef, useImperativeHandle, useState } from "react";
import styled from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import {
    getSummaryData,
    getTotalPrice,
    getShowPrice,
    getShowCancelButton,
    getSummaryItems,
} from "../../store/sagas/selectors";
import { ABORT_CONFIGURATION } from "../../store/actions/externalActions";
import { SummaryItems } from "./SummaryItems";
import { usePriceFormatter } from "../../utilities/priceFormatter";
import { useBreakpoint } from "../../utilities/media-js-breakpoints";
import { useText } from "../../utilities/textUtil";

interface ContainerProps {
    withCancel: boolean;
}

const BaseContainer = styled.div<ContainerProps>`
    position: absolute;
    bottom: 10px;
    right: 10px;
    ${({ theme }) => theme.bp.md`
        bottom: 25px;
        right: 25px;
    `}
    background: #fff;
    border-radius: 4px;
    box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.13);
    z-index: 15;
    display: flex;
    flex-direction: column;
    align-content: stretch;
    align-items: flex-start;
    justify-content: space-between;
    font-size: 12px;
`;

const Container = styled(BaseContainer)`
    width: 250px;
    ${({ theme }) => theme.bp.md`
        width: 300px;
    `}
    padding-bottom: ${({ withCancel }) => (withCancel ? "15px" : "25px")};
`;

const MinimizedContainer = styled(BaseContainer)`
    width: 130px;
`;

const ButtonContainer = styled.div`
    width: 100%;
`;

const ContinueButton = styled.div`
    width: calc(100% - 50px);
    height: 40px;
    padding: 0 14px 0 10px;
    ${({ theme }) => theme.bp.md`
        width: calc(100% - 80px);
        padding: 0 16px;
        height: 48px;
    `}
    background: rgba(0, 0, 0, 1);
    margin: 0 auto;
    border-radius: 4px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.2s ease-in-out;
    box-sizing: border-box;
    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }

    &:active {
        background: ${({ theme }) => get(theme, "filter.chips.backgroundColor", "#223861")};
    }

    & .material-icons {
        flex-basis: 35px;
    }

    &.minimized {
        margin-bottom: 10px;
        width: calc(100% - 20px);
        box-sizing: border-box;
    }
`;

const ContinueText = styled.div`
    flex-grow: 1;
`;

const CancelButton = styled.div`
    height: 30px;
    width: calc(100% - 80px);
    margin: 5px auto 0;
    border-radius: 4px;
    color: var(--primary-color);
    text-align: center;
    cursor: pointer;
    font-size: 14px;
    transition: background 0.2s ease-in-out;
    padding: 4px 16px 0;
    box-sizing: border-box;
    &:hover {
        background: var(--light-hover-color);
    }

    &:active {
        background: var(--light-active-color);
    }

    &.minimized {
        margin: 0 auto;
        width: calc(100% - 20px);
    }
`;

const MinimizedArticles = styled.div`
    padding: 15px 10px 10px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    & .material-icons {
        position: relative;
        top: -2px;
        vertical-align: middle;
    }
`;

// Use forwardRef to get the ref object to extend with public methods.
export const Summary = forwardRef(({ onContinue }: { onContinue: () => void }, ref) => {
    const dispatch = useDispatch();
    const breakpoints = useBreakpoint();
    const summaryData = useSelector(getSummaryData);
    const totalPrice = useSelector(getTotalPrice);
    const showPrice = useSelector(getShowPrice);
    const showCancelButton = useSelector(getShowCancelButton);
    const summaryItems = useSelector(getSummaryItems);
    const t = useText();
    const priceFormatter = usePriceFormatter(false);

    const [expanded, setExpanded] = useState(false);

    const minimized = !breakpoints.md && !expanded;
    const totalPriceText = priceFormatter.formatPrice(totalPrice);

    // Add a public minimize method.
    useImperativeHandle(ref, () => ({
        setExpanded,
    }));

    const renderContinueButton = (withPrice: boolean) => {
        return (
            <ContinueButton className={minimized ? "minimized" : "expanded"} onClick={onContinue}>
                <span className="material-icons">arrow_forward</span>
                <ContinueText>{t("summaryContinue")}</ContinueText>
                {withPrice && showPrice && summaryData?.totalPrice ? <span>{totalPriceText}</span> : null}
            </ContinueButton>
        );
    };

    const renderCancelButton = () => {
        return (
            <CancelButton
                className={minimized ? "minimized" : "expanded"}
                onClick={() => dispatch({ type: ABORT_CONFIGURATION })}
            >
                {t("abortConfiguration")}
            </CancelButton>
        );
    };

    if (minimized) {
        const numItems = summaryItems.reduce((agg, item) => agg + item.amount, 0);
        return (
            <MinimizedContainer withCancel={false}>
                <MinimizedArticles onClick={() => setExpanded(true)}>
                    <span className="material-icons">keyboard_arrow_up</span>
                    {numItems} {numItems === 1 ? t("summaryNumItemsSingular") : t("summaryNumItemsPlural")}
                </MinimizedArticles>
                {renderContinueButton(false)}
                {showCancelButton && renderCancelButton()}
            </MinimizedContainer>
        );
    }

    return (
        <Container withCancel={showCancelButton}>
            <SummaryItems />
            <ButtonContainer>
                {renderContinueButton(true)}
                {showCancelButton && renderCancelButton()}
            </ButtonContainer>
        </Container>
    );
});

Summary.displayName = "Summary";
